import ContentLoader from 'stimulus-content-loader'
import {startIcons} from '../import_lucid.js'
import {queueDashboardRequest} from '../dashboard_request_queue.js'

export default class extends ContentLoader {

  static values = {
    url: String,
    lazyLoading: Boolean,
    lazyLoadingThreshold: Number,
    lazyLoadingRootMargin: {
      type: String,
      default: '0px'
    },
    refreshInterval: Number,
    loadScripts: Boolean,
    replaceHtml: {
      type: Boolean,
      default: false
    },
    debounceTime: Number,
    queued: Boolean
  }

  disconnect() {
    clearTimeout(this.debounceTimer)
    this.visibilityObserver?.disconnect()
    this.visibilityObserver = null
    this.abortController?.abort()
    super.disconnect()
  }

  lazyLoad() {
    this.isVisible = false
    this.visibilityObserver = new IntersectionObserver((entries, observer) => {
      if (this.visibilityObserver !== observer || !this.element.isConnected) return

      for (const entry of entries) {
        this.isVisible = entry.isIntersecting
        if (this.isVisible) this.fetch()
      }
    }, {
      threshold: this.lazyLoadingThresholdValue,
      rootMargin: this.lazyLoadingRootMarginValue
    })
    this.visibilityObserver.observe(this.element)
  }

  fetch() {
    if (!this.urlValue) return;
    if (this.visibilityObserver && !this.isVisible) return

    if (this.hasDebounceTimeValue && this.debounceTimeValue > 0) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => this.performFetch(), this.debounceTimeValue)
    } else {
      this.performFetch();
    }
  }

  performFetch() {
    if (!this.urlValue || !this.element.isConnected) return
    if (this.visibilityObserver && !this.isVisible) return

    const url = this.urlValue
    this.urlValue = undefined
    this.abortController?.abort()
    const controller = new AbortController()
    this.abortController = controller
    const request = () => {
      if (controller.signal.aborted || !this.element.isConnected) return null
      // Visibility may have changed while waiting for a dashboard queue slot.
      if (this.visibilityObserver && !this.isVisible) {
        this.urlValue = url
        return null
      }
      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect()
        this.visibilityObserver = null
        if (this.hasRefreshIntervalValue) this.startRefreshing()
      }
      return fetch(url, {signal: controller.signal})
        .then((response) => response.text())
    }

    const response = this.queuedValue
        ? queueDashboardRequest(request, controller.signal)
        : request()

    return response
        .then((html) => {
          if (html === null || controller.signal.aborted || !this.element.isConnected) return
          if (this.replaceHtmlValue) {
            this.element.outerHTML = html;
          } else {
            this.element.innerHTML = html;
          }
          if (this.loadScriptsValue) {
            this.loadScripts();
          }
          startIcons()
        })
        .catch((error) => {
          if (controller.signal.aborted) return
          console.error("Error fetching data:", error);
        });
  }
}
